import React from 'react';
import { graphql } from 'react-relay';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useSegmentationTargetGroupsQuery$data,
	useSegmentationTargetGroupsQuery$variables,
} from './__generated__/useSegmentationTargetGroupsQuery.graphql.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
type UseSegmentationTargetGroupsProps = {
	initialGroupIds: string[];
	nextCursor?: string;
};

type UseSegmentationTargetGroupsReturnType = {
	data?: useSegmentationTargetGroupsQuery$data;
	loading: boolean;
	errorComponent?: JSX.Element;
};

export const useSegmentationTargetGroups = ({
	nextCursor,
	initialGroupIds,
}: UseSegmentationTargetGroupsProps): UseSegmentationTargetGroupsReturnType => {
	const { data, loading, error } = useQueryInterop<
		useSegmentationTargetGroupsQuery$data,
		useSegmentationTargetGroupsQuery$variables
	>(
		graphql`
			query useSegmentationTargetGroupsQuery($after: String, $first: Int = 25) {
				groups(after: $after, first: $first) {
					nodes {
						id
						name
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`,
		{
			variables: {
				after: nextCursor,
			},
			skip: initialGroupIds.length === 0,
		},
	);

	let errorComponent: undefined | JSX.Element = undefined;
	if (error && !loading) {
		if (isUnauthorizedError(error)) {
			markErrorAsHandled(error);
		} else {
			errorComponent = <ErrorDisplay error={error} />;
		}
	}

	return { data, loading, errorComponent };
};
